/**
 * The price band — the one place its numbers are written. The website's pricing page and JSON-LD
 * offers, the support FAQ, the trial banner's plan table (desktop, web app, office phone) and the
 * server's upgrade-request email all read these, so a price changes here and nowhere else.
 *
 * Priced on fleet size, never per user: drivers (the driver app) and office users cost nothing
 * extra. Fleet size is the organisation's own trucks plus its hired work, where
 * `HIRED_TRIPS_PER_TRUCK` hired trips a month count as one truck, so a broker with no trucks pays
 * for the trips it runs. Each size comes in two editions: Essentials keeps the books, and Complete
 * adds the automation for one fixed yearly price per size band (`automationYearlyFor`). Every
 * amount is in rupees and is what the customer pays while `GST_REGISTERED` is false. There is no setup fee. Each extra truck costs less the
 * larger the fleet, and one more truck never makes the bill smaller. The Microsoft Store listing
 * quotes no price (`pricing.microsoftStore` is Free).
 */
import { pricing } from './identity.js';
export const isPlanEdition = (value) => value === 'essentials' || value === 'complete';
/**
 * Whether the business is registered for GST (owner, 2026-11-01: not yet, until revenue nears the
 * ₹20 lakh threshold). An unregistered business may not charge GST, so the prices below are the
 * whole amount. On registering, set this true: every surface then says GST is extra.
 */
export const GST_REGISTERED = false;
/** GST charged on top of every price below once registered, in percent. */
export const GST_PERCENT = 18;
/** The sentence about GST that goes with every published price. */
export const gstTerms = GST_REGISTERED
    ? `All prices exclude GST (${GST_PERCENT}%).`
    : 'We do not charge GST at present, so the price shown is what you pay. If we register for GST later, it will be added as the law requires.';
/** This many hired trips a month count as one truck. */
export const HIRED_TRIPS_PER_TRUCK = 15;
export const freePlan = {
    id: 'free',
    name: 'Free',
    minFleetSize: 0,
    maxFleetSize: 2,
};
export const starterPlan = {
    id: 'starter',
    name: 'Starter',
    minFleetSize: 3,
    maxFleetSize: 5,
    yearly: 6999,
    monthly: 749,
    automationYearly: 2999,
};
export const businessPlan = {
    id: 'business',
    name: 'Business',
    minFleetSize: 6,
    maxFleetSize: 10,
    yearly: 9999,
    monthly: 1049,
    automationYearly: 4999,
};
export const fleetPlan = {
    id: 'fleet',
    name: 'Fleet',
    minFleetSize: 11,
    maxFleetSize: 50,
    slabs: [
        { upTo: 25, perTruckYearly: 800, perTruckMonthly: 85, automationYearly: 7999 },
        { upTo: 50, perTruckYearly: 500, perTruckMonthly: 55, automationYearly: 12999 },
    ],
};
export const largeFleetPlan = {
    id: 'large',
    name: 'Large fleet',
    minFleetSize: 51,
    fromPerExtraTruckYearly: 350,
    automationYearly: 19999,
};
/**
 * What Complete adds to Essentials: the features that save staff time rather than keep the books.
 * `AUTOMATION_ACTIONS` in `@transport/contracts/contracts/organization` is what it unlocks.
 */
export const completeEdition = {
    name: 'Complete',
    essentialsName: 'Essentials',
    /** What Essentials is for: the books. */
    essentialsIncludes: [
        'Trips, LRs and invoices',
        'Full accounting and settlements',
        'GST documents',
        'Job cards, inventory and the driver app',
    ],
    includes: [
        'Bank statement download',
        'FASTag and fuel card sync',
        'Leakage Detector and Business Health',
        'Lane profitability and driver scorecards',
    ],
};
/** In the order a page lists them. */
export const plans = [freePlan, starterPlan, businessPlan, fleetPlan, largeFleetPlan];
export const PLAN_IDS = plans.map(plan => plan.id);
export function isPlanId(value) {
    return typeof value === 'string' && PLAN_IDS.includes(value);
}
export function planById(id) {
    return plans.find(plan => plan.id === id);
}
const wholeCount = (value) => (Number.isFinite(value) && value > 0 ? Math.ceil(value) : 0);
/** Own trucks plus hired trips as trucks. A part of `HIRED_TRIPS_PER_TRUCK` counts as a whole one. */
export function fleetSize(trucks, hiredTripsPerMonth = 0) {
    return wholeCount(trucks) + Math.ceil(wholeCount(hiredTripsPerMonth) / HIRED_TRIPS_PER_TRUCK);
}
/** What Complete adds at a fleet size, or null on Free, which cannot take it. */
export function automationYearlyFor(size) {
    const trucks = wholeCount(size);
    if (trucks <= freePlan.maxFleetSize)
        return null;
    if (trucks <= starterPlan.maxFleetSize)
        return starterPlan.automationYearly;
    if (trucks <= businessPlan.maxFleetSize)
        return businessPlan.automationYearly;
    const slab = fleetPlan.slabs.find(({ upTo }) => trucks <= upTo);
    return slab ? slab.automationYearly : largeFleetPlan.automationYearly;
}
/** What the slabs add at a fleet size, by the rate each truck falls in. */
function slabTotal(size, rate) {
    let total = 0;
    let from = businessPlan.maxFleetSize;
    for (const slab of fleetPlan.slabs) {
        total += Math.max(0, Math.min(size, slab.upTo) - from) * rate(slab);
        from = slab.upTo;
    }
    return total;
}
const fleetYearlyAt = (size) => businessPlan.yearly + slabTotal(size, slab => slab.perTruckYearly);
const fleetMonthlyAt = (size) => businessPlan.monthly + slabTotal(size, slab => slab.perTruckMonthly);
/** The slabs as words: "₹800 per truck for trucks 11–25 + ₹500 per truck for trucks 26–50". */
function slabWords(rate) {
    let from = businessPlan.maxFleetSize;
    return fleetPlan.slabs
        .map(slab => {
        const words = `${formatInr(rate(slab))} per truck for trucks ${from + 1}–${slab.upTo}`;
        from = slab.upTo;
        return words;
    })
        .join(' + ');
}
/**
 * The plan that fits and its prices: Free up to 2 (0 included, so a new trial with nothing entered
 * still sees a price), Starter to 5, Business to 10, Fleet to 50, and Large fleet above that, by
 * quote. A count that is not a finite number reads as 0; a fraction rounds up.
 */
export function quoteForTrucks(trucks, hiredTripsPerMonth = 0) {
    const size = {
        trucks: wholeCount(trucks),
        hiredTripsPerMonth: wholeCount(hiredTripsPerMonth),
        fleetSize: fleetSize(trucks, hiredTripsPerMonth),
    };
    const automation = automationYearlyFor(size.fleetSize);
    if (automation === null) {
        return { plan: 'free', ...size, yearly: 0, monthly: 0, completeYearly: null };
    }
    for (const plan of [starterPlan, businessPlan]) {
        if (size.fleetSize <= plan.maxFleetSize) {
            return {
                plan: plan.id,
                ...size,
                yearly: plan.yearly,
                monthly: plan.monthly,
                completeYearly: plan.yearly + automation,
            };
        }
    }
    if (size.fleetSize <= fleetPlan.maxFleetSize) {
        const yearly = fleetYearlyAt(size.fleetSize);
        return {
            plan: 'fleet',
            ...size,
            yearly,
            monthly: fleetMonthlyAt(size.fleetSize),
            completeYearly: yearly + automation,
        };
    }
    const fromYearly = fleetYearlyAt(fleetPlan.maxFleetSize) +
        (size.fleetSize - fleetPlan.maxFleetSize) * largeFleetPlan.fromPerExtraTruckYearly;
    return {
        plan: 'large',
        ...size,
        yearly: null,
        monthly: null,
        fromYearly,
        fromCompleteYearly: fromYearly + automation,
    };
}
/** A yearly price as rupees per truck per day, one decimal: "₹2.6". */
export function perTruckPerDay(yearly, size) {
    const trucks = Math.max(1, wholeCount(size));
    return `₹${(yearly / trucks / 365).toFixed(1)}`;
}
/**
 * Rupees with Indian digit grouping (₹7,999, ₹10,800, ₹1,08,000), written out rather than through
 * `Intl`, which a phone's JavaScript engine may not carry with the `en-IN` locale.
 */
export function formatInr(amount) {
    const whole = Math.round(Math.abs(amount)).toString();
    const lastThree = whole.slice(-3);
    const rest = whole.slice(0, -3);
    const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${lastThree}` : lastThree;
    return `${amount < 0 ? '-' : ''}₹${grouped}`;
}
const hiredTripsAt = (size) => size * HIRED_TRIPS_PER_TRUCK;
/** Who each plan is for, as one line. */
export function planTrucksLabel(id) {
    if (id === 'free') {
        return `Up to ${freePlan.maxFleetSize} trucks or ${hiredTripsAt(freePlan.maxFleetSize)} hired trips a month`;
    }
    if (id === 'large') {
        return `${largeFleetPlan.minFleetSize}+ trucks or more than ${hiredTripsAt(fleetPlan.maxFleetSize)} hired trips a month`;
    }
    const plan = id === 'starter' ? starterPlan : id === 'business' ? businessPlan : fleetPlan;
    return `${plan.minFleetSize}–${plan.maxFleetSize} trucks or up to ${hiredTripsAt(plan.maxFleetSize)} hired trips a month`;
}
/** Each plan's prices, as the pages and the plan table show them. `complete` is absent on Free. */
export function planPriceLines(id) {
    if (id === 'free') {
        return { price: '₹0, with no end date', alternative: 'No card and no payment details' };
    }
    if (id === 'starter' || id === 'business') {
        const plan = id === 'starter' ? starterPlan : businessPlan;
        return {
            price: `${formatInr(plan.yearly)} per year`,
            alternative: `or ${formatInr(plan.monthly)} per month billed monthly`,
            complete: `${formatInr(plan.yearly + plan.automationYearly)} per year`,
        };
    }
    if (id === 'fleet') {
        const [first, second] = fleetPlan.slabs;
        return {
            price: `${formatInr(businessPlan.yearly)} per year + ${slabWords(slab => slab.perTruckYearly)}`,
            alternative: `or ${formatInr(businessPlan.monthly)} per month + ${slabWords(slab => slab.perTruckMonthly)}, billed monthly`,
            complete: `Essentials + ${formatInr(first.automationYearly)} per year up to ${first.upTo} trucks, ${formatInr(second.automationYearly)} up to ${second.upTo}`,
        };
    }
    return {
        price: `From ${formatInr(fleetYearlyAt(fleetPlan.maxFleetSize))} per year + ${formatInr(largeFleetPlan.fromPerExtraTruckYearly)} per truck above ${fleetPlan.maxFleetSize}`,
        alternative: 'By quote, for the size of your fleet',
        complete: `Essentials + ${formatInr(largeFleetPlan.automationYearly)} per year`,
    };
}
function describeSize(quote) {
    const parts = [];
    if (quote.trucks > 0 || quote.hiredTripsPerMonth === 0) {
        parts.push(`${quote.trucks} ${quote.trucks === 1 ? 'truck' : 'trucks'}`);
    }
    if (quote.hiredTripsPerMonth > 0) {
        parts.push(`${quote.hiredTripsPerMonth} hired ${quote.hiredTripsPerMonth === 1 ? 'trip' : 'trips'} a month`);
    }
    return parts.join(' and ');
}
/**
 * A quote as one line: "Fleet for 11 trucks — Complete ₹18,798 per year, Essentials ₹10,799 per
 * year (or ₹1,134 per month billed monthly)".
 */
export function describeQuote(quote) {
    const name = planById(quote.plan).name;
    const size = describeSize(quote);
    if (quote.plan === 'free')
        return `${name} — ₹0 for ${size}`;
    if (quote.plan === 'large') {
        return `${name} for ${size} — by quote, from ${formatInr(quote.fromCompleteYearly)} per year for Complete, ${formatInr(quote.fromYearly)} for Essentials`;
    }
    return `${name} for ${size} — Complete ${formatInr(quote.completeYearly)} per year, Essentials ${formatInr(quote.yearly)} per year (or ${formatInr(quote.monthly)} per month billed monthly)`;
}
/** A fleet this far above what it paid for waits until renewal: the larger of the two. */
export const FLEET_GROWTH_MIN_TRUCKS = 5;
export const FLEET_GROWTH_PERCENT = 20;
/** Growth beyond that has this long to pay the difference before new trucks are held back. */
export const FLEET_GROWTH_PAY_DAYS = 30;
export const fleetGrowthAllowance = (paidFleetSize) => Math.max(FLEET_GROWTH_MIN_TRUCKS, Math.ceil((paidFleetSize * FLEET_GROWTH_PERCENT) / 100));
export const isFleetOverPaid = (paidFleetSize, size) => size > paidFleetSize + fleetGrowthAllowance(paidFleetSize);
/** A quote's yearly price in an edition; a quoted plan's is its "from" price. Null on Free. */
export function yearlyForEdition(quote, edition) {
    if (quote.plan === 'free')
        return null;
    if (quote.plan === 'large') {
        return edition === 'complete' ? quote.fromCompleteYearly : quote.fromYearly;
    }
    return edition === 'complete' ? quote.completeYearly : quote.yearly;
}
/** The sizes a higher plan is offered at: the most each flat plan covers, and two steps of Fleet. */
const PLAN_CHOICE_SIZES = [
    starterPlan.maxFleetSize,
    businessPlan.maxFleetSize,
    25,
    fleetPlan.maxFleetSize,
];
/**
 * What someone may choose to pay for instead of the plan they fit: never less, and never a Large
 * fleet, which is a quote. A size on the same flat plan costs the same, so it is no choice at all.
 */
export function higherPlanSizes(size) {
    const fits = quoteForTrucks(size).plan;
    return PLAN_CHOICE_SIZES.filter(choice => {
        if (choice <= size)
            return false;
        return fits === 'fleet' || quoteForTrucks(choice).plan !== fits;
    });
}
/** What growth costs for the days left of a paid year: the yearly difference, pro rata. */
export const differenceAmount = (newYearly, paidYearly, daysLeft) => Math.max(0, Math.round(((newYearly - paidYearly) * daysLeft) / 365));
/** Hired trips a month as a broker's prices, for the pricing page's table. */
export const brokerExamples = [30, 75, 150, 300, 500, 750].map(trips => quoteForTrucks(0, trips));
/** What a fleet pays at a few sizes, for the pricing page's table. */
export const fleetExamples = [5, 10, 25, 50, 100].map(trucks => quoteForTrucks(trucks));
/** The terms that go with every price, in the order a page lists them. */
export const pricingNotes = {
    gst: gstTerms,
    setupFee: 'No setup fee.',
    perTruck: 'Priced on fleet size, not per user: drivers on the driver app and office users are included at no extra charge.',
    hiredTrips: `Hired work counts too: every ${HIRED_TRIPS_PER_TRUCK} hired trips a month count as one truck, averaged over 3 months. Empty runs and cancelled trips do not count.`,
    complete: 'Complete is billed yearly. Essentials can be billed yearly or monthly.',
    higherPlan: 'You can choose a higher plan than your fleet needs. It then covers that many trucks, so you can grow into it with nothing more to pay.',
    growth: `If your fleet grows during the year by up to ${FLEET_GROWTH_MIN_TRUCKS} trucks or ${FLEET_GROWTH_PERCENT}%, whichever is larger, nothing changes until renewal. Beyond that you pay the difference for the months left, and past ${fleetPlan.maxFleetSize} trucks we agree a quote with you. After ${FLEET_GROWTH_PAY_DAYS} days unpaid, new trucks cannot be added until it is settled; everything you already have keeps working.`,
    trial: `Every organisation starts with a ${pricing.freeTrialDays}-day free trial with everything switched on. When it ends, a fleet within the Free limit moves to Free and keeps working; a larger one stays readable and can export until it chooses a plan.`,
};
/** What we promise whoever signs up, on the pricing page and in the FAQ. */
export const pricingPromises = [
    {
        title: 'Your data stays yours',
        body: 'You can always sign in, look at your records and export them, even if you stop paying.',
    },
    {
        title: 'No lockout after the trial',
        body: 'A small fleet moves to Free and keeps working. Nobody loses access to what they entered.',
    },
    {
        title: 'Your rate stays locked',
        body: 'Your per-truck rate does not rise for as long as you stay subscribed.',
    },
    {
        title: 'No surprise bills',
        body: 'Small changes in your fleet wait until renewal. If your fleet grows a lot, we tell you first and you pay only the difference for the months left.',
    },
    {
        title: 'Simple refunds',
        body: `Ask within ${pricing.refundDays} days of paying and we refund you, with no questions asked.`,
    },
    {
        title: 'No extra charge for people',
        body: 'No setup fee. Drivers and everyone in the office are free, on every plan.',
    },
];
//# sourceMappingURL=plans.js.map